import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IBranchModel } from 'src/app/logic/branch/models/branch.model';
import { BranchesService } from 'src/app/services/branches.service';
import { HttpService } from 'src/app/services/http-service.service';

interface GroupedBranches {
  city: string;
  branches: IBranchModel[];
}

@Component({
  selector: 'app-branch-select-field',
  templateUrl: './branch-select-field.component.html',
  styleUrls: ['./branch-select-field.component.scss'],
})
export class BranchSelectFieldComponent implements OnInit, OnDestroy {
  public isMenuOpen = false;
  public selectedBranch: IBranchModel | null = null;
  public groupedBranches: GroupedBranches[] = [];
  public allBranches: IBranchModel[] = [];

  private branchIdSub?: Subscription;
  private branchesLoadSub?: Subscription;
  private selectedBranchSub?: Subscription;

  constructor(
    private readonly branchesService: BranchesService,
    private readonly httpService: HttpService,
  ) {}

  ngOnInit(): void {
    this.loadBranches();

    this.selectedBranchSub = this.branchesService
      .getBranchSelected()
      .subscribe((branch) => {
        if (branch) {
          this.selectedBranch = branch;
        }
      });

    this.branchIdSub = this.branchesService.sucursalId.subscribe(
      (branchId: number) => {
        if (!branchId || !this.allBranches.length) {
          return;
        }
        const found = this.allBranches.find((branch) => branch.id === branchId);
        if (found) {
          this.selectedBranch = found;
        }
      },
    );
  }

  ngOnDestroy(): void {
    this.branchIdSub?.unsubscribe();
    this.branchesLoadSub?.unsubscribe();
    this.selectedBranchSub?.unsubscribe();
  }

  get displayLabel(): string {
    if (!this.selectedBranch) {
      return '';
    }
    const city =
      this.selectedBranch.address?.city ||
      this.selectedBranch.address?.locality ||
      '';
    if (!city) {
      return this.selectedBranch.name || '';
    }
    return `${this.selectedBranch.name} - ${city}`;
  }

  toggleMenu(event?: Event): void {
    event?.stopPropagation();
    this.isMenuOpen = !this.isMenuOpen;
  }

  selectBranch(branch: IBranchModel, event?: Event): void {
    event?.stopPropagation();
    this.selectedBranch = branch;
    this.branchesService.setBranchSelected(branch);
    this.persistUserBranch(branch.id);
    this.isMenuOpen = false;
  }

  private persistUserBranch(branchId: number): void {
    try {
      const current = JSON.parse(localStorage.getItem('current') || '{}');
      if (!current?.id || !branchId) {
        return;
      }
      this.httpService.assignUserBranch(current.id, branchId).subscribe({
        error: () => {
          /* local selection already applied */
        },
      });
    } catch {
      /* ignore */
    }
  }

  isSelected(branch: IBranchModel): boolean {
    return this.selectedBranch?.id === branch.id;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.isMenuOpen) {
      this.closeMenu();
    }
  }

  private loadBranches(): void {
    this.branchesLoadSub = this.branchesService.getAll().subscribe({
      next: (branches) => {
        this.allBranches = branches;
        this.groupBranchesByCity();
        if (this.selectedBranch?.id) {
          const found = branches.find(
            (branch) => branch.id === this.selectedBranch?.id,
          );
          if (found) {
            this.selectedBranch = found;
          }
        }
      },
      error: (error) => {
        console.error('Error loading branches:', error);
      },
    });
  }

  private groupBranchesByCity(): void {
    const grouped: { [key: string]: IBranchModel[] } = {};

    this.allBranches.forEach((branch) => {
      const city = branch.address?.locality || branch.address?.city || 'Sin ciudad';
      if (!grouped[city]) {
        grouped[city] = [];
      }
      grouped[city].push(branch);
    });

    this.groupedBranches = Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map((city) => ({
        city,
        branches: grouped[city],
      }));
  }
}
